
import Vue from 'vue'
import Menu from '@/components/admin/Menu.vue'
import {
  LIST_PARAGON_DEFAULT_PRESERVE_QUERY_KEYS,
  adminPathForTab,
  adminTabIndexFromPath,
  adminTabKeyFromIndex,
  adminTabKeyFromPath,
  pickContextRouteQuery,
  routeQueriesEqual,
  routeQueryToStringRecord,
} from '@/utils/listParagonRouteQuery'
import type { AdminTabIndex, RouteQuery } from '@/utils/listParagonRouteQuery'

type TabRouteQueries = Record<AdminTabIndex, Record<string, string> | null>

const ADMIN_TAB_CONTEXT_KEYS = [...LIST_PARAGON_DEFAULT_PRESERVE_QUERY_KEYS] as const

function emptyTabRouteQueries(): TabRouteQueries {
  return { 0: null, 1: null, 2: null, 3: null, 4: null }
}

export default Vue.extend({
  components: {
    Menu
  },
  data: () => ({
    tab: 'spaces',
    adminTab: 0 as AdminTabIndex,
    tabRouteQueries: emptyTabRouteQueries() as TabRouteQueries,
  }),
  computed: {
    tabTitle() {
      return this.$vuetify.lang.t("$vuetify.admin." + this.tab +".title")
    }
  },
  watch: {
    '$route.path': {
      handler() {
        this.syncAdminTabFromRoute()
      },
      immediate: true,
    },
    '$route.query': {
      handler() {
        this.persistActiveAdminTabRouteQuery()
      },
      deep: true,
    },
  },
  created() {
    document.title = this.$vuetify.lang.t("$vuetify.tab_titles.admin")
    this.syncAdminTabFromRoute()
    this.persistActiveAdminTabRouteQuery()
  },
  methods: {
    persistActiveAdminTabRouteQuery() {
      if (!this.$route) return
      const tab = adminTabIndexFromPath(this.$route.path)
      this.tabRouteQueries = {
        ...this.tabRouteQueries,
        [tab]: routeQueryToStringRecord(this.$route.query as RouteQuery),
      }
    },
    syncAdminTabFromRoute() {
      if (!this.$route) return
      const tabFromPath = adminTabIndexFromPath(this.$route.path)
      if (this.adminTab !== tabFromPath) {
        this.adminTab = tabFromPath
      }
      this.tab = adminTabKeyFromPath(this.$route.path)
    },
    onAdminTabInput(tab: AdminTabIndex) {
      if (this.adminTab === tab) return
      this.adminTab = tab
      this.syncRouteFromAdminTab(tab)
    },
    onAdminTabTitleChange(tab: AdminTabIndex) {
      this.tab = adminTabKeyFromIndex(tab)
    },
    syncRouteFromAdminTab(tab: AdminTabIndex) {
      if (!this.$router || !this.$route) return

      const currentTab = adminTabIndexFromPath(this.$route.path)
      this.tabRouteQueries = {
        ...this.tabRouteQueries,
        [currentTab]: routeQueryToStringRecord(this.$route.query as RouteQuery),
      }

      const targetPath = adminPathForTab(tab)
      const storedQuery = this.tabRouteQueries[tab]
      if (storedQuery == null && targetPath === this.$route.path) {
        return
      }
      const targetQuery = storedQuery ?? pickContextRouteQuery(
        this.$route.query as RouteQuery,
        ADMIN_TAB_CONTEXT_KEYS,
      )
      const currentQuery = routeQueryToStringRecord(this.$route.query as RouteQuery)

      if (this.$route.path !== targetPath || !routeQueriesEqual(currentQuery, targetQuery)) {
        const replaceResult = this.$router.replace({
          path: targetPath,
          query: targetQuery,
        })
        if (replaceResult && typeof replaceResult.catch === 'function') {
          replaceResult.catch(() => undefined)
        }
      }
    },
  },
})
